/**
 * Moz JSON-RPC Client (Story 20.7, AC #5)
 *
 * The shared JSON-RPC 2.0 envelope client used by every Moz V3 method helper
 * in `api.ts`. This client owns THREE Moz V3 quirks (Round 2 Pre-mortem):
 *
 *  1. **`id` MUST be ≥ 24 chars** — V3 spec requires this even though JSON-RPC
 *     2.0 does not. Source moz.com/api/docs/guides/getting-started verified
 *     2026-05-10. The helper generates a UUIDv4 (32 hex chars + 4 dashes = 36
 *     chars, comfortably above 24).
 *
 *  2. **Async-call envelope** — methods that perform expensive operations
 *     (Brand Authority, full Site Crawl exports) return `result.async_call.id`
 *     immediately and the actual result is fetched via a follow-up call to
 *     `data.async-call.fetch`. This client detects the pattern via
 *     `result.async_call !== undefined` and routes to `pollMozAsyncCall`
 *     automatically — the caller of `mozJsonRpcCall` receives the final
 *     unwrapped result, never the async-call envelope.
 *
 *  3. **Error envelope shape** — V3 errors return
 *     `{ id, jsonrpc, error: { code, status, data: { explanation, issue, key }, message } }`
 *     instead of the JSON-RPC 2.0 standard `{ jsonrpc, id, error: {code, message, data}}`.
 *     The helper detects errors via `result.error !== undefined` and throws
 *     `MozApiCallError` with the full structured fields preserved.
 *
 * **Output unwrapping:** the JSON-RPC 2.0 spec dictates the actual result
 * lives at `response.result`. Moz V3 conforms — every method response has the
 * shape `{ id, jsonrpc, result: {...} }`. Callers of `mozJsonRpcCall<TParams,
 * TResult>(...)` receive `response.result` directly (the `TResult` type
 * parameter is the SHAPE OF `result`, NOT the SHAPE OF the full envelope).
 */

import type { AdapterEnv } from "../adapter";
import {
  MozApiCallError,
  MozAsyncTimeoutError,
  parseMozHttpError,
  parseMozJsonRpcError,
  type MozJsonRpcErrorEnvelope,
} from "./errors";
import { safeFetchMoz, type SafeFetchMozOptions } from "./safe-fetch";

// ============================================================================
// Constants
// ============================================================================

/** The single universal JSON-RPC endpoint for Moz V3 (per moz.com/api/docs verified 2026-05-10). */
export const MOZ_JSON_RPC_BASE = "https://api.moz.com/jsonrpc";

/**
 * Closed enumeration of catalogued Moz V3 method names. Story 20.7 ships
 * 5 of these in production helpers (the rest are catalogued for Story 20.7a /
 * 20.8 expansion). Every helper in `api.ts` hardcodes its `method` value as
 * a string literal — typos surface as TypeScript errors via this closed enum.
 */
export const MOZ_METHOD_NAMES = [
  // Site metrics
  "data.site.metrics.fetch",
  "data.site.metrics.fetchMultiple",
  "data.site.metrics.distributions.fetch",
  "data.site.metrics.histories.fetch",
  "data.site.metrics.brand.authority.fetch",
  "beta.data.metrics.brand.authority.generate",
  // Top pages
  "data.site.top-pages.list",
  // Linking root domains
  "data.site.linking-root-domains.list",
  "data.site.linking-root-domains.count",
  // Anchor text
  "data.site.anchor-text.list",
  // Links
  "data.site.links.list",
  "data.site.link-status.list",
  "data.site.link-intersect.list",
  "data.site.final-redirect.fetch",
  // Global rankings
  "data.global.topRankingDomains",
  "data.global.topRankingPages",
  // Keyword data (Story 20.7a/20.8)
  "data.keyword.metrics.fetch.all",
  "data.keyword.metrics.fetch.difficulty",
  "data.keyword.metrics.fetch.opportunity",
  "data.keyword.metrics.fetch.priority",
  "data.keyword.metrics.fetch.volume",
  "data.keyword.searchIntent.fetch",
  "data.keyword.relatedKeywords.list",
  // Site keyword rankings
  "data.site.ranking-keywords.list",
  "data.site.ranking-keywords.count",
  // Operational
  "quota.lookup",
  "metadata.indexId.fetch",
  "data.async-call.fetch",
] as const;

export type MozMethodName = (typeof MOZ_METHOD_NAMES)[number];

/**
 * Async-call polling backoff schedule (AC #5).
 *
 * 1s → 2s → 4s → 8s → 15s = 30 sec total budget per moz.com/api/docs/guides/error-handling
 * verified 2026-05-10. After the final 15s wait the helper throws
 * `MozAsyncTimeoutError` rather than block the data-sync queue indefinitely.
 *
 * Polling is NOT free — Moz charges 1 row per `data.async-call.fetch` call
 * (per AC #4 Method Group B). The 5-step backoff caps total polling cost at
 * 5 rows per Brand Authority fetch in the worst case.
 */
export const MOZ_ASYNC_POLL_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000] as const;

// ============================================================================
// Types
// ============================================================================

export interface MozJsonRpcResponseSuccess<TResult> {
  readonly id: string;
  readonly jsonrpc: "2.0";
  readonly result: TResult;
}

export interface MozJsonRpcResponseError {
  readonly id: string;
  readonly jsonrpc: "2.0";
  readonly error?: undefined;
  readonly result: { readonly error: MozJsonRpcErrorEnvelope };
}

export interface MozAsyncCallEnvelope {
  readonly async_call: {
    readonly id: string;
    readonly result?: unknown;
    readonly status?: string;
  };
}

export interface MozJsonRpcCallOptions {
  /** Optional integrationId wired through to safeFetchMoz for per-integration cooldown tracking. */
  readonly integrationId?: string;
  /**
   * Optional clock override for test determinism (used by polling backoff).
   * If omitted, `Date.now`/`setTimeout` are used.
   */
  readonly now?: () => number;
  /** Optional setTimeout override for test determinism. */
  readonly sleep?: (ms: number) => Promise<void>;
  /**
   * **Round 1 review fix (AC #5 mandatory):** optional dot-separated path into
   * `result` whose presence is asserted post-unwrap. e.g. `"site.metrics"` for
   * `data.site.metrics.fetch`. When the path is missing or undefined the call
   * throws `MozApiCallError` with `type: "unknown"` so silent response-shape
   * changes by Moz cannot propagate downstream as null/zero metric values.
   */
  readonly expectedResultPath?: string;
  /**
   * Optional out-param: a mutable `{ count: number }` object the helper
   * increments by the number of `data.async-call.fetch` polls performed (each
   * poll costs 1 row per moz.com/api/docs/guides/error-handling). Lets the
   * caller record the true row cost in `MozHelperResult.rowsCharged` instead
   * of hard-coding a conservative estimate.
   */
  readonly pollCount?: { count: number };
}

// ============================================================================
// UUIDv4 generator (AC #5)
// ============================================================================

/**
 * Generate a UUIDv4 for the JSON-RPC `id` field. Cloudflare Workers + Node
 * 19+ both expose `crypto.randomUUID` per webcrypto spec; the result is 36
 * chars (32 hex + 4 dashes), comfortably above Moz's 24-char minimum.
 *
 * Exported for tests asserting length invariant.
 */
export function generateMozRpcId(): string {
  return crypto.randomUUID();
}

// ============================================================================
// Async-call detection + polling (AC #5)
// ============================================================================

function isAsyncCallEnvelope(value: unknown): value is MozAsyncCallEnvelope {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { async_call?: unknown };
  if (v.async_call === undefined || v.async_call === null) return false;
  const ac = v.async_call as { id?: unknown };
  return typeof ac.id === "string";
}

/**
 * Round 1 review fix (AC #5) — assert that `value[path]` is non-undefined.
 * Throws `MozApiCallError` (type=unknown, retryable=true) if the path is
 * missing — defends against silent response-shape changes by Moz where a
 * caller would otherwise consume a partially-populated record as truth.
 *
 * Path is dot-separated: `"site.metrics"` → `value.site.metrics`.
 */
function assertExpectedResultPath(value: unknown, path: string): void {
  // Round 2 review fix — validate path syntax up-front to defend against
  // callers passing malformed paths (empty string, leading ".", trailing ".",
  // consecutive "..") which would otherwise produce empty segments and throw
  // confusing "broke at ''" errors.
  if (path.length === 0) {
    throw new TypeError("assertExpectedResultPath: path must be non-empty.");
  }
  const segments = path.split(".");
  for (const seg of segments) {
    if (seg.length === 0) {
      throw new TypeError(
        `assertExpectedResultPath: path '${path}' contains an empty segment (leading/trailing '.' or consecutive '..').`,
      );
    }
  }

  let cursor: unknown = value;
  for (const key of segments) {
    if (typeof cursor !== "object" || cursor === null) {
      throw new MozApiCallError({
        type: "unknown",
        code: null,
        message: `Moz response missing expected path '${path}' (broke at '${key}'). Response shape may have changed; treating as transient.`,
        status: 200,
        retryable: true,
      });
    }
    cursor = (cursor as Record<string, unknown>)[key];
  }
  if (cursor === undefined || cursor === null) {
    throw new MozApiCallError({
      type: "unknown",
      code: null,
      message: `Moz response missing expected path '${path}'. Response shape may have changed; treating as transient.`,
      status: 200,
      retryable: true,
    });
  }
}

/**
 * Default sleep helper — bounded by the 15-sec final backoff step so the
 * Worker doesn't exceed its 30-sec CPU budget in the worst case.
 */
function defaultSleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll a Moz async-call until it resolves or the polling budget is exhausted.
 *
 * @param asyncCallId - The `result.async_call.id` from the initial JSON-RPC response.
 * @param decryptedToken - The Moz API token for `x-moz-token` header.
 * @param env - Adapter env (unused at this layer; reserved for future KV-cached polling state).
 * @param options - Optional integrationId + sleep/now overrides for tests.
 * @returns The resolved `async_call.result` (typed as unknown — caller casts).
 * @throws MozAsyncTimeoutError if the call doesn't resolve within ~30 sec.
 * @throws MozApiCallError on JSON-RPC error envelope from any poll attempt.
 */
export async function pollMozAsyncCall(
  asyncCallId: string,
  decryptedToken: string,
  _env: AdapterEnv,
  options: MozJsonRpcCallOptions = {},
): Promise<unknown> {
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const startedAt = now();

  for (const backoffMs of MOZ_ASYNC_POLL_BACKOFF_MS) {
    await sleep(backoffMs);

    // Probe via `data.async-call.fetch` — the Moz V3 method for polling.
    // Each probe costs 1 row per moz.com/api/docs/guides/error-handling
    // verified 2026-05-10 (polling is NOT free).
    const result = await mozJsonRpcCallRaw<{ id: string }, MozAsyncCallEnvelope>(
      "data.async-call.fetch",
      { id: asyncCallId },
      decryptedToken,
      options,
    );
    // Round 1 review fix (out-param): bubble the actual poll count back to the
    // caller so MozHelperResult.rowsCharged can record the true cost (was
    // hard-coded to 2 in fetchMozBrandAuthority before).
    if (options.pollCount) {
      options.pollCount.count++;
    }

    // **Round 3 review fix:** detect terminal FAILURE status from Moz so we
    // don't surface a phantom result that downstream code transforms into a
    // null-score record. Moz uses async_call.status to indicate job state
    // (DONE / PENDING / RUNNING / FAILED / TIMEOUT / ERROR per typical
    // job-queue conventions; specifics vary). Treat anything matching the
    // FAILED-class regex as a terminal error.
    const status = result.async_call.status;
    if (typeof status === "string" && /^(failed|error|timeout)$/i.test(status)) {
      throw new MozApiCallError({
        type: "provider_unavailable",
        code: status,
        message: `Moz async-call ${asyncCallId} reported terminal status '${status}' — treating as transient.`,
        status: 502,
        retryable: true,
      });
    }

    // **Round 3 review fix:** also reject empty-object results — Moz's
    // backend can serialize an in-progress job with an initialized-but-empty
    // result holder. Polling should continue rather than exit on garbage.
    const r = result.async_call.result;
    if (r !== undefined && r !== null) {
      const isEmptyObject =
        typeof r === "object" && !Array.isArray(r) && Object.keys(r as Record<string, unknown>).length === 0;
      if (!isEmptyObject) {
        return r;
      }
    }
    // Otherwise loop and back-off again.
  }

  throw new MozAsyncTimeoutError(asyncCallId, now() - startedAt);
}

// ============================================================================
// Raw JSON-RPC call (no async-call recursion — used by the poll loop itself)
// ============================================================================

/**
 * Internal: perform a single JSON-RPC POST without auto-routing async-call
 * envelopes (the poll loop calls this directly to avoid infinite recursion).
 */
async function mozJsonRpcCallRaw<TParams extends Record<string, unknown>, TResult>(
  method: MozMethodName,
  params: TParams,
  decryptedToken: string,
  options: MozJsonRpcCallOptions = {},
): Promise<TResult> {
  const id = generateMozRpcId();
  const body = JSON.stringify({
    jsonrpc: "2.0",
    id,
    method,
    params: { data: params },
  });

  const fetchOptions: SafeFetchMozOptions = {
    ...(options.integrationId !== undefined ? { integrationId: options.integrationId } : {}),
    ...(options.now !== undefined ? { now: options.now } : {}),
  };

  const result = await safeFetchMoz(
    MOZ_JSON_RPC_BASE,
    decryptedToken,
    { method: "POST", body },
    fetchOptions,
  );

  if (!result.success) {
    // Network / DNS / abort error — surface as a synthetic 5xx-equivalent so
    // the caller's catch-block routes to provider_unavailable retryable.
    throw new MozApiCallError({
      type: "provider_unavailable",
      code: null,
      message: result.error.message,
      status: 502,
      retryable: true,
    });
  }

  const response = result.data;
  const responseStatus = response.status;

  // Read the body ONCE (Response is a stream; we may need it for both
  // success-envelope unwrapping AND HTTP-status-only error parsing).
  let bodyText: string;
  try {
    bodyText = await response.text();
  } catch (err) {
    throw new MozApiCallError({
      type: "provider_unavailable",
      code: null,
      message: `Failed to read Moz response body: ${
        err instanceof Error ? err.message : String(err)
      }`,
      status: responseStatus,
      retryable: true,
    });
  }

  // HTTP-level error path (4xx/5xx) — Moz typically wraps errors in the
  // JSON-RPC envelope but fronting CDN/gateway failures may surface as
  // raw HTTP status with a non-JSON body.
  if (responseStatus < 200 || responseStatus >= 300) {
    const httpError = parseMozHttpError(responseStatus, bodyText, decryptedToken);
    if (httpError) {
      throw new MozApiCallError(httpError);
    }
  }

  // Parse JSON-RPC envelope.
  let envelope: { id?: string; jsonrpc?: string; result?: unknown; error?: unknown };
  try {
    envelope = JSON.parse(bodyText);
  } catch {
    throw new MozApiCallError({
      type: "unknown",
      code: null,
      message: `Moz returned non-JSON body (status ${responseStatus}). Treating as transient.`,
      status: responseStatus,
      retryable: true,
    });
  }

  // JSON-RPC 2.0 standard error envelope check (in case Moz sends the standard
  // shape rather than their non-standard `result.error` shape).
  if (
    envelope.error !== undefined &&
    envelope.error !== null &&
    typeof envelope.error === "object"
  ) {
    const parsed = parseMozJsonRpcError(
      envelope.error as MozJsonRpcErrorEnvelope,
      responseStatus,
      decryptedToken,
    );
    throw new MozApiCallError(parsed);
  }

  // Moz's documented non-standard error shape: error nested under `result.error`.
  if (
    envelope.result !== undefined &&
    envelope.result !== null &&
    typeof envelope.result === "object" &&
    "error" in (envelope.result as Record<string, unknown>) &&
    (envelope.result as { error?: unknown }).error !== undefined
  ) {
    const parsed = parseMozJsonRpcError(
      (envelope.result as { error: MozJsonRpcErrorEnvelope }).error,
      responseStatus,
      decryptedToken,
    );
    throw new MozApiCallError(parsed);
  }

  // Happy path — `result` is the unwrapped value of type TResult.
  if (envelope.result === undefined || envelope.result === null) {
    throw new MozApiCallError({
      type: "unknown",
      code: null,
      message: `Moz response missing 'result' field (status ${responseStatus}). Method: ${method}.`,
      status: responseStatus,
      retryable: true,
    });
  }

  return envelope.result as TResult;
}

// ============================================================================
// Public mozJsonRpcCall (auto-routes async-call envelopes per AC #5)
// ============================================================================

/**
 * Make a JSON-RPC 2.0 call to Moz V3.
 *
 * @param method - The Moz method name (closed enum; typo-checked at compile time).
 * @param params - The `params.data` payload (the JSON-RPC client wraps this in
 *   `{ jsonrpc: "2.0", id, method, params: { data: ... } }`).
 * @param decryptedToken - The Moz API token for `x-moz-token` header.
 * @param options - Optional integrationId for cooldown tracking + test overrides.
 * @returns The unwrapped `result` of type TResult. If Moz returns a
 *   `result.async_call.id` envelope, this helper polls until resolution and
 *   returns the final result (typed as unknown — caller casts).
 * @throws MozApiCallError on any JSON-RPC error envelope or HTTP error.
 * @throws MozAsyncTimeoutError if an async-call doesn't resolve within ~30 sec.
 *
 * **Type safety note:** TResult is the shape of `response.result`. If the call
 * triggers async-call routing, the actual result type is `unknown` and the
 * caller MUST cast/validate. We can't statically know whether a method returns
 * sync or async because Moz documents both modes for some methods (e.g.
 * Brand Authority is always async; URL Metrics is always sync).
 */
export async function mozJsonRpcCall<TParams extends Record<string, unknown>, TResult>(
  method: MozMethodName,
  params: TParams,
  decryptedToken: string,
  env: AdapterEnv,
  options: MozJsonRpcCallOptions = {},
): Promise<TResult> {
  const result = await mozJsonRpcCallRaw<TParams, unknown>(
    method,
    params,
    decryptedToken,
    options,
  );

  // Auto-route async-call envelopes per AC #5.
  if (isAsyncCallEnvelope(result)) {
    // If `result.async_call.result` is already populated, the call resolved
    // synchronously — return it without polling.
    if (
      result.async_call.result !== undefined &&
      result.async_call.result !== null
    ) {
      if (options.expectedResultPath) {
        assertExpectedResultPath(
          result.async_call.result,
          options.expectedResultPath,
        );
      }
      return result.async_call.result as TResult;
    }
    const polled = await pollMozAsyncCall(
      result.async_call.id,
      decryptedToken,
      env,
      options,
    );
    return polled as TResult;
  }

  // Round 1 review fix (AC #5 expectedResultPath): assert shape on sync results.
  if (options.expectedResultPath) {
    assertExpectedResultPath(result, options.expectedResultPath);
  }

  return result as TResult;
}
